'use client'

import { Cell, Spacer, Text } from '@vinted/web-ui'
import { useEffect, useState } from 'react'

import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { Translation } from '@marketplace-web/i18n/i18n-data'
import { LinkifiedMessage } from '@marketplace-web/linkified-message/linkified-message-feature'

import { TranslationControl } from '@marketplace-web/i18n/translations-feature'
import { getUserById, ProfileUserDto } from '@marketplace-web/profile/profile-data'

type Props = {
  user: ProfileUserDto
  isContentTranslationEnabled: boolean
  viewingSelf?: boolean
}

const MAX_SYMBOL_COUNT = 220

function truncateDescription(description: string) {
  return description.substring(0, MAX_SYMBOL_COUNT).split('\n')[0]!.trim()
}

const UserDescription = ({ user, isContentTranslationEnabled, viewingSelf }: Props) => {
  const translate = useTranslate('user.description')
  const breakpoints = useBreakpoint()
  const [isTruncated, setIsTruncated] = useState(false)
  const [isContentTranslated, setIsContentTranslated] = useState(false)
  const [translatedDescription, setTranslatedDescription] = useState('')

  const profileReworkAbTest = useAbTest('profile_wardrobe_web_rework')
  const isProfileReworkTestOn = profileReworkAbTest?.variant === 'on' && !breakpoints.portables

  const { fetch: fetchUserById, data: userData } = useFetch(getUserById)

  useEffect(() => {
    if (!user.about) return

    const truncatedDescription = truncateDescription(user.about)

    if (user.about !== truncatedDescription) {
      setIsTruncated(true)
    }
  }, [user.about])

  useEffect(() => {
    if (!userData) return

    const { about } = userData.user

    if (!about) return

    setTranslatedDescription(about)
    setIsContentTranslated(true)
  }, [userData])

  async function translateDescription() {
    if (translatedDescription) {
      setIsContentTranslated(true)

      return
    }

    await fetchUserById({ id: user.id, localize: true })
  }

  function showOriginal() {
    setIsContentTranslated(false)
  }

  function handleTranslate(isTranslated: boolean) {
    return isTranslated ? showOriginal() : translateDescription()
  }

  function toggleIsTruncated() {
    setIsTruncated(prevIsTruncated => !prevIsTruncated)
  }

  const fullDescription =
    isContentTranslated && translatedDescription ? translatedDescription : user.about

  if (!fullDescription) return null

  const activeDescription = isTruncated
    ? `${truncateDescription(fullDescription)}...`
    : fullDescription
  const showTranslationControl =
    isContentTranslationEnabled && user.localization !== Translation.None

  return (
    <Cell
      styling={isProfileReworkTestOn && viewingSelf ? Cell.Styling.Tight : Cell.Styling.Default}
    >
      <Text as="span" format>
        <div className="profile__user-description" data-testid="profile-user-description">
          <LinkifiedMessage
            text={activeDescription}
            trackingData={{
              user_id: user.id.toString(),
            }}
          />
          {isTruncated && (
            <button
              type="button"
              className="u-ui-margin-left-small"
              onClick={toggleIsTruncated}
              data-testid="more-button"
            >
              <Text as="span" clickable>
                {translate('actions.more')}
              </Text>
            </button>
          )}
        </div>
      </Text>
      {showTranslationControl && (
        <>
          <Spacer />
          <TranslationControl
            isTranslated={isContentTranslated}
            autotranslate={false}
            onClick={handleTranslate}
            tracking={{
              target: 'translate_description',
              targetDetails: user.id.toString(),
            }}
          />
        </>
      )}
    </Cell>
  )
}

export default UserDescription
